/**
 * Database Field Translation Utility
 * 
 * This utility provides functions to translate database fields based on
 * translation scope parameters. Each field can be marked as translatable
 * or not, allowing fine-grained control over what gets translated.
 */

import { useCallback } from 'react'
import { useTranslationToggle } from '@/contexts/TranslationContext'

/**
 * Interface for defining which fields should be translated
 */
export interface TranslationScope {
    [fieldName: string]: boolean
}

/**
 * Interface for database objects with translatable fields
 */
export interface TranslatableRecord {
    [key: string]: any
}

/**
 * Hook for translating database records with field-level control
 */
export function useDatabaseTranslation() {
    const { translateText, isTranslationEnabled } = useTranslationToggle()

    /**
     * Translate a single database record based on translation scope
     */
    const translateRecord = useCallback(async (
        record: TranslatableRecord,
        translationScope: TranslationScope,
        sourceLanguage?: string
    ): Promise<TranslatableRecord> => {
        if (!isTranslationEnabled || !record) {
            return record
        }

        const translatedRecord = { ...record }

        // Process each field in the translation scope
        for (const [fieldName, shouldTranslate] of Object.entries(translationScope)) {
            if (shouldTranslate && record[fieldName] && typeof record[fieldName] === 'string') {
                try {
                    // Preserve original value for fields that might be needed for logic (e.g., system role checks)
                    const originalValue = record[fieldName]
                    if (fieldName === 'name' && !translatedRecord._originalName) {
                        (translatedRecord as any)._originalName = originalValue
                    }
                    const translatedText = await translateText(originalValue, sourceLanguage)
                    translatedRecord[fieldName] = translatedText
                } catch (error) {
                    console.error(`Translation error for field ${fieldName}:`, error)
                    // Keep original text if translation fails
                }
            }
        }

        return translatedRecord
    }, [isTranslationEnabled, translateText])

    /**
     * Translate an array of database records
     */
    const translateRecords = useCallback(async (
        records: TranslatableRecord[],
        translationScope: TranslationScope,
        sourceLanguage?: string
    ): Promise<TranslatableRecord[]> => {
        if (!isTranslationEnabled || !records || records.length === 0) {
            return records
        }

        const translatedRecords = await Promise.all(
            records.map(record => translateRecord(record, translationScope, sourceLanguage))
        )

        return translatedRecords
    }, [isTranslationEnabled, translateRecord])

    /**
     * Translate nested objects (e.g., user roles, permissions)
     */
    const translateNestedRecord = useCallback(async (
        record: TranslatableRecord,
        translationScope: TranslationScope,
        nestedScopes?: { [nestedKey: string]: TranslationScope },
        sourceLanguage?: string
    ): Promise<TranslatableRecord> => {
        if (!isTranslationEnabled || !record) {
            return record
        }

        // First translate the main record
        let translatedRecord = await translateRecord(record, translationScope, sourceLanguage)

        // Then translate nested objects if specified
        if (nestedScopes) {
            for (const [nestedKey, nestedScope] of Object.entries(nestedScopes)) {
                if (record[nestedKey] && Array.isArray(record[nestedKey])) {
                    // Handle arrays of nested objects
                    translatedRecord[nestedKey] = await translateRecords(
                        record[nestedKey],
                        nestedScope,
                        sourceLanguage
                    )
                } else if (record[nestedKey] && typeof record[nestedKey] === 'object') {
                    // Handle single nested object
                    translatedRecord[nestedKey] = await translateRecord(
                        record[nestedKey],
                        nestedScope,
                        sourceLanguage
                    )
                }
            }
        }

        return translatedRecord
    }, [isTranslationEnabled, translateRecord, translateRecords])

    return {
        translateRecord,
        translateRecords,
        translateNestedRecord,
        isTranslationEnabled,
    }
}

/**
 * Predefined translation scopes for common database entities
 */
export const TRANSLATION_SCOPES = {
    // User entity translation scope
    USER: {
        name: true,           // User names should be translated
        description: true,    // User descriptions should be translated
        email: false,         // Email addresses should NOT be translated
        language: false,      // Language codes should NOT be translated
        timezone: false,      // Timezone should NOT be translated
        avatar_url: false,    // URLs should NOT be translated
        created_at: false,    // Timestamps should NOT be translated
        updated_at: false,    // Timestamps should NOT be translated
    } as TranslationScope,

    // Role entity translation scope
    ROLE: {
        name: true,           // Role names should be translated
        description: true,    // Role descriptions should be translated
        id: false,            // IDs should NOT be translated
        created_at: false,    // Timestamps should NOT be translated
        updated_at: false,    // Timestamps should NOT be translated
    } as TranslationScope,

    // Permission entity translation scope
    PERMISSION: {
        name: true,           // Permission names should be translated
        resource: true,       // Resource names should be translated
        action: true,         // Action names should be translated
        id: false,            // IDs should NOT be translated
        role_id: false,       // Foreign keys should NOT be translated
        created_at: false,    // Timestamps should NOT be translated
    } as TranslationScope,

    // Job entity translation scope
    JOB: {
        name: true,           // Job names should be translated
        description: true,    // Job descriptions should be translated
        status: true,         // Status messages should be translated
        id: false,            // IDs should NOT be translated
        user_id: false,       // Foreign keys should NOT be translated
        created_at: false,    // Timestamps should NOT be translated
        updated_at: false,    // Timestamps should NOT be translated
        last_run: false,      // Timestamps should NOT be translated
        next_run: false,      // Timestamps should NOT be translated
    } as TranslationScope,

    // Vault entity translation scope
    VAULT: {
        name: true,           // Vault names should be translated
        description: true,    // Vault descriptions should be translated
        type: true,           // Vault types should be translated
        id: false,            // IDs should NOT be translated
        url: false,           // URLs should NOT be translated
        created_at: false,    // Timestamps should NOT be translated
        updated_at: false,    // Timestamps should NOT be translated
    } as TranslationScope,
} as const

/**
 * Nested translation scopes for complex objects
 */
export const NESTED_TRANSLATION_SCOPES = {
    // User with roles
    USER_WITH_ROLES: {
        roles: TRANSLATION_SCOPES.ROLE,
    },

    // Role with permissions
    ROLE_WITH_PERMISSIONS: {
        permissions: TRANSLATION_SCOPES.PERMISSION,
    },

    // User with roles and permissions
    USER_WITH_ROLES_AND_PERMISSIONS: {
        roles: {
            ...TRANSLATION_SCOPES.ROLE,
            permissions: TRANSLATION_SCOPES.PERMISSION,
        },
    },
} as const

/**
 * Utility function to create custom translation scopes
 */
export function createTranslationScope(fields: string[], translatable: boolean = true): TranslationScope {
    const scope: TranslationScope = {}
    fields.forEach(field => {
        scope[field] = translatable
    })
    return scope
}

/**
 * Utility function to merge translation scopes
 */
export function mergeTranslationScopes(...scopes: TranslationScope[]): TranslationScope {
    return scopes.reduce((merged, scope) => ({ ...merged, ...scope }), {})
}

/**
 * Type-safe wrapper for database translation
 */
export function withDatabaseTranslation<T extends TranslatableRecord>(
    data: T | T[],
    translationScope: TranslationScope,
    sourceLanguage?: string
) {
    const { translateRecord, translateRecords } = useDatabaseTranslation()

    if (Array.isArray(data)) {
        return translateRecords(data, translationScope, sourceLanguage)
    } else {
        return translateRecord(data, translationScope, sourceLanguage)
    }
}

